import type { MenuProps } from "antd";
type MenuItem = Required<MenuProps>["items"][number];
import {
  EditOutlined,
  FolderOpenOutlined,
  AppstoreOutlined,
  ToolOutlined,
  ScissorOutlined,
} from "@ant-design/icons";
import { Robot2Line } from "@/components/icon/RobotIcon";
import { KeyRound, SellerIcon, ChannelsIcon } from "@/components";
import { nivelDeOperadores } from "@/utilities/nivel-de-operadores";

type MenuItemWithList = Omit<MenuItem, "children"> & {
  name?: string;
  list?: string;
  items?: MenuItemWithList[];
  key?: string;
  label?: string;
  type?: string;
  meta?: {};
};

/**
 * ¿Ve el árbol de categorías de ESTE store?
 *
 * El permiso `categorias` es de tipo Store, así que vive en
 * `permissions.store.categorias[<storeCode>]`. **Se mira el store del menú, no cualquiera**:
 * tenerlo en un store no dice nada sobre el árbol de otro — el API lo rechaza igual, pero
 * el ítem no tiene que ofrecerse. Alcanza con `viewer`: qué se puede cambiar adentro lo
 * decide la pantalla.
 *
 * Sale de la sesión, que se congela al login: recién concedido, hay que re-loguear para que
 * aparezca. El default es `no_access` (falla cerrado).
 */
const veCategorias = (identity: any, storeCode: string): boolean => {
  const nivel = String(
    identity?.permissions?.store?.categorias?.[storeCode] ?? "",
  )
    .trim()
    .toLowerCase();

  return nivel !== "" && nivel !== "no_access";
};

/**
 * ¿Ve Apps → Optimizer de ESTE store? (xcons-docs#524). `store_optimizer_config` es binario y
 * arranca en Sin acceso: sólo lo ve quien lo tenga concedido.
 */
const veOptimizador = (identity: any, storeCode: string): boolean => {
  const nivel = String(
    identity?.permissions?.store?.store_optimizer_config?.[storeCode] ??
      "no_access",
  )
    .trim()
    .toLowerCase();

  return nivel !== "no_access";
};

export const ResourcesStores = (t: any, identity?: any) => {
  const sellerLinkStorage =
    localStorage.getItem("smartMenuName")?.replace(/"/g, "").toLowerCase() ??
    "";
  const sellerLink = encodeURIComponent(sellerLinkStorage);

  const resourcesOrders: MenuItemWithList[] = [
    // {
    //   key: "stores/design",
    //   name: "stores/design",
    //   meta: {
    //     label: t("smartMenu.storeLabel.design"),
    //     icon: <EditOutlined />,
    //   },
    //   items: [
    //     {
    //       key: "stores/pages",
    //       list: `stores/pages/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.pages"),
    //       },
    //     },
    //     {
    //       key: "stores/files",
    //       list: `stores/files/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.files"),
    //       },
    //     },
    //     {
    //       key: "stores/blocks",
    //       list: `stores/blocks/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.blocks"),
    //       },
    //     },
    //     {
    //       key: "stores/menus",
    //       list: `stores/menus/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.menus"),
    //       },
    //     },
    //     {
    //       key: "stores/templates",
    //       list: `stores/templates/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.templates"),
    //       },
    //     },
    //     {
    //       key: "stores/settings",
    //       list: `stores/settings/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.settings"),
    //       },
    //     },
    //   ],
    // },
    // {
    //   key: "stores/catalog",
    //   name: "stores/catalog",
    //   meta: {
    //     label: t("smartMenu.storeLabel.catalog"),
    //     icon: <FolderOpenOutlined />,
    //   },
    //   items: [
    //     {
    //       key: "stores/catalog-simple-products",
    //       list: `stores/catalog-simple-products/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.simpleProducts"),
    //       },
    //     },
    //     {
    //       key: "stores/sets",
    //       list: `stores/sets/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.sets"),
    //       },
    //     },
    //     {
    //       key: "stores/assistants",
    //       list: `stores/assistants/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.assistants"),
    //       },
    //     },
    //     {
    //       key: "stores/labels",
    //       list: `stores/labels/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.labels"),
    //       },
    //     },
    //     {
    //       key: "stores/catalog-settings",
    //       list: `stores/catalog-settings/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.configs"),
    //       },
    //     },

    //     {
    //       key: "stores/calculators",
    //       list: `stores/calculators/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.calculators"),
    //       },
    //     },
    //     {
    //       key: "stores/categories",
    //       list: `stores/categories/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.categories"),
    //       },
    //     },
    //   ],
    // },
    // {
    //   key: "stores/applications",
    //   name: "stores/applications",
    //   meta: {
    //     label: t("smartMenu.storeLabel.applications"),
    //     icon: <AppstoreOutlined />,
    //   },
    //   items: [
    //     {
    //       key: "stores/rfm-segmentation",
    //       list: `stores/rfm-segmentation/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.segmentation"),
    //       },
    //     },
    //     {
    //       key: "stores/loyalty",
    //       list: `stores/loyalty/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.loyalty"),
    //       },
    //     },
    //     {
    //       key: "stores/app-automations",
    //       list: `stores/app-automations/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.automations"),
    //       },
    //     },
    //     {
    //       key: "stores/google",
    //       list: `stores/google/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.google"),
    //       },
    //     },
    //     {
    //       key: "stores/meta",
    //       list: `stores/meta/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.meta"),
    //       },
    //     },
    //     {
    //       key: "stores/leptom-opt",
    //       list: `stores/leptom-opt/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.leptomOpt"),
    //       },
    //     },
    //     {
    //       key: "stores/full-reporting",
    //       list: `stores/full-reporting/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.fullReporting"),
    //       },
    //     },
    //     {
    //       key: "stores/email",
    //       list: `stores/email/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.email"),
    //       },
    //     },
    //     {
    //       key: "stores/whatsapp-qr",
    //       list: `stores/whatsapp-qr/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.whatsappQR"),
    //       },
    //     },
    //     {
    //       key: "stores/whatsapp-api",
    //       list: `stores/whatsapp-api/${sellerLink}`,
    //       meta: {
    //         label: t("smartMenu.storeLabel.whatsappApi"),
    //       },
    //     },
    //   ],
    // },
    {
      key: "stores/automations",
      name: "stores/automations",
      list: `stores/automations/${sellerLink}`,
      meta: {
        label: t("smartMenu.storeLabel.automations"),
        icon: <Robot2Line />,
      },
    },
    {
      key: `stores/linked-sellers`,
      name: `stores/linked-sellers`,
      list: `stores/linked-sellers/${sellerLink}`,
      meta: {
        label: t("smartMenu.storeLabel.linkedSellers"),
        icon: <SellerIcon style={{ transform: "scale(1.1)" }} />,
      },
    },
    // Operadores del Store se ofrece sólo con `store_operators` distinto de No en ESTE store
    // (xcons-docs#537). Viewer lo ve igual que Admin: la diferencia está adentro, en que no
    // puede guardar. Se saca del array, como Categorías, porque `CanAccess` deja pasar todo.
    ...(nivelDeOperadores(identity, sellerLinkStorage) !== "no_access"
      ? [
          {
            key: `stores/operators`,
            name: `stores/operators`,
            list: `stores/operators/${sellerLink}`,
            meta: {
              label: t("smartMenu.storeLabel.storeOperators"),
              icon: <KeyRound />,
            },
          },
        ]
      : []),
    // Asistente IA (xcons-docs#566): con el mismo gate que la API, `store_operators` distinto de No.
    ...(nivelDeOperadores(identity, sellerLinkStorage) !== "no_access"
      ? [
          {
            key: `stores/agent`,
            name: `stores/agent`,
            list: `stores/agent/${sellerLink}`,
            meta: {
              label: t("smartMenu.storeLabel.agent", "Asistente IA"),
              icon: <Robot2Line />,
            },
          },
        ]
      : []),
    // Canales es la configuración de Conversaciones.
    {
      key: `stores/channels`,
      name: `stores/channels`,
      list: `stores/channels/${sellerLink}`,
      meta: {
        label: t("smartMenu.storeLabel.channels", "Channels"),
        icon: <ChannelsIcon />,
      },
    },
    {
      key: `stores/stores-settings`,
      name: `stores/stores-settings`,
      list: `stores/stores-settings/${sellerLink}`,
      meta: {
        label: t("smartMenu.storeLabel.storeSettings"),
        icon: <ToolOutlined />,
      },
    },
  ];

  // Se saca del array en vez de esconderse con `CanAccess`, igual que el Catálogo en
  // `resources.tsx`: este admin no tiene `accessControlProvider` y `CanAccess` deja pasar todo.
  if (veCategorias(identity, sellerLinkStorage)) {
    resourcesOrders.push({
      key: `stores/categories`,
      name: `stores/categories`,
      list: `stores/categories/${sellerLink}`,
      meta: {
        label: t("smartMenu.storeLabel.categories", "Categorías"),
        icon: <FolderOpenOutlined />,
      },
    });
  }

  if (veOptimizador(identity, sellerLinkStorage)) {
    resourcesOrders.push({
      key: "stores/apps",
      name: "stores/apps",
      meta: {
        label: t("smartMenu.label.apps", "Aplicaciones"),
        icon: <AppstoreOutlined />,
      },
      items: [
        {
          key: "stores/optimizer",
          name: "stores/optimizer",
          list: `stores/optimizer/${sellerLink}`,
          meta: {
            label: t("smartMenu.label.optimizer", "Optimizador"),
            icon: <ScissorOutlined />,
          },
        },
      ],
    });
  }

  return resourcesOrders;
};
